import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Save, Loader2, Music4 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

export default function ManualSong() {
  const navigate = useNavigate();
  const [title, setTitle] = useState("");
  const [artist, setArtist] = useState("");
  const [key, setKey] = useState("");
  const [category, setCategory] = useState("");
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);

  const inp = "bg-background border-border rounded-xl";

  const save = async () => {
    if (!title.trim()) return toast.error("Informe o título da música.");
    setSaving(true);
    try {
      let structured = null;
      if (text.trim()) {
        try {
          const { data: fmt } = await api.post("/songs/format", { text, title: title.trim(), artist: artist.trim(), key: key.trim(), use_ai: true, source: "user_content" });
          if (fmt?.sections?.length) structured = { original_key: fmt.original_key || key || "", current_key: fmt.original_key || key || "", sections: fmt.sections, source: "user_content" };
        } catch { /* mantém texto puro se a formatação falhar */ }
      }
      const body = { title: title.trim(), artist: artist.trim(), key: key.trim(), category: category.trim(), lyrics: text, chords: "", notes: "", tags: [] };
      if (structured) body.structured = structured;
      const { data: song } = await api.post("/songs", body);
      toast.success("Música salva na biblioteca!");
      navigate(`/repertorio/musica/${song.id}`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="max-w-3xl mx-auto pb-10" data-testid="manual-song-page">
      <div className="sticky top-0 z-20 -mx-4 px-4 py-2.5 bg-background/95 backdrop-blur border-b border-border flex items-center justify-between gap-2">
        <Button data-testid="ms-back" onClick={() => navigate("/repertorio")} className="rounded-lg gap-1.5 bg-primary text-primary-foreground hover:bg-primary/90"><ArrowLeft className="h-4 w-4" />Voltar</Button>
        <Button data-testid="ms-save" onClick={save} disabled={saving} className="rounded-lg gap-2 bg-success text-success-foreground hover:opacity-90">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}Salvar na biblioteca</Button>
      </div>

      <div className="mt-5 flex items-center gap-3">
        <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-accent/15 text-accent shrink-0"><Music4 className="h-6 w-6" /></span>
        <div>
          <h1 className="font-head text-2xl font-extrabold tracking-tight">Nova música</h1>
          <p className="text-sm text-muted-foreground">Preencha os dados e a cifra. Salvamos na sua biblioteca.</p>
        </div>
      </div>

      <div className="mt-5 grid grid-cols-1 sm:grid-cols-6 gap-3">
        <div className="sm:col-span-3"><label className="text-xs text-muted-foreground">Título *</label><Input data-testid="ms-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Ex: Evidências" className={inp} autoFocus /></div>
        <div className="sm:col-span-2"><label className="text-xs text-muted-foreground">Artista</label><Input data-testid="ms-artist" value={artist} onChange={(e) => setArtist(e.target.value)} placeholder="Ex: Chitãozinho & Xororó" className={inp} /></div>
        <div className="sm:col-span-1"><label className="text-xs text-muted-foreground">Tom</label><Input data-testid="ms-key" value={key} onChange={(e) => setKey(e.target.value)} placeholder="G" className={inp} /></div>
        <div className="sm:col-span-3"><label className="text-xs text-muted-foreground">Categoria (opcional)</label><Input data-testid="ms-category" value={category} onChange={(e) => setCategory(e.target.value)} placeholder="Ex: Sertanejo" className={inp} /></div>
      </div>

      <div className="mt-4">
        <label className="text-xs text-muted-foreground">Letra / cifra (acordes acima das linhas)</label>
        <Textarea data-testid="ms-text" value={text} onChange={(e) => setText(e.target.value)} placeholder={"[Intro]\nG  D  Em  C\n\nG            D\nTrecho da letra aqui..."} className="bg-background border-border rounded-xl font-mono text-sm min-h-[55vh] mt-1" />
      </div>
    </div>
  );
}
